import { Button, Form, Layout, Tag, Tooltip, message } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { SDetailLayout } from 'app/components/StaycationUI';
import { MenuCustom } from 'app/components/commons/MenuCustom/MenuCustom';
import { Notification } from 'app/components/commons/Notification/Notification';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import { UserContext } from 'app/context/UserContext/UserContext';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import {
  useGetHotel,
  useGetPackageByHotelId,
  useUpdateHotel,
} from 'app/hooks/data/hotels/useHotel';
import { useCountries } from 'app/hooks/data/useCountries';
import { ChannelManager } from 'app/typings/channelManagers';
import { ApiError } from 'app/typings/error';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { RateMode } from 'app/typings/rateModes';
import type { HotelErrorCode } from 'app/utils/errors/HotelErrors';
import { retrieveHotelErrorMessage } from 'app/utils/errors/HotelErrors';

import { Copywriting } from '../HotelDetail/Copywriting/Copywriting';
import { CoverAndDiscover } from '../HotelDetail/CoverAndDiscover/CoverAndDiscover';
import { Finance } from '../HotelDetail/Finance/Finance';
import { MarketingBrief } from '../HotelDetail/MarketingBrief/MarketingBrief';
import { Services } from '../HotelDetail/Services/Services';
import { Spaces } from '../HotelDetail/Spaces/Spaces';

import { BasicInfo } from './BasicInfo';
import { DuplicateModal } from './DuplicateModal/DuplicateModal';
import './HotelDetail.scss';
import { MigrateModal } from './MigrateModal/MigrateModal';
import { PackagesRoomsAmenities } from './PackagesRoomsAmenities/PackagesRoomsAmenities';

const { Content } = Layout;

enum MenuItemName {
  General = 'General',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Services = 'Services',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Spaces = 'Spaces',
  Brief = 'Brief',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Finance = 'Finance',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Copywriting = 'Copywriting',
  Cover = 'Cover & Discover',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  PackagesRoomsAmenities = 'Packages, Rooms & Amenities',
}

type MenuItem = {
  name: MenuItemName;
};

const MENU_ITEMS = [
  { name: MenuItemName.PackagesRoomsAmenities },
  { name: MenuItemName.General },
  { name: MenuItemName.Finance },
  { name: MenuItemName.Services },
  { name: MenuItemName.Spaces },
  { name: MenuItemName.Brief },
  { name: MenuItemName.Copywriting },
  { name: MenuItemName.Cover },
];

const STANDALONE_MENUS = new Set([
  MenuItemName.Finance,
  MenuItemName.Copywriting,
  MenuItemName.PackagesRoomsAmenities,
]);

const HotelEditContent = () => {
  const { id: hotelId } = useParams<{ id: string }>();
  const { data: packagesList } = useGetPackageByHotelId(Number(hotelId));

  const history = useHistory();
  const location = useLocation();
  const {
    data: hotel,
    isFetching: isFetchHotelLoading,
    refetch: reloadHotel,
  } = useGetHotel(+hotelId);
  const { mutateAsync: updateHotel, error: editHotelError } = useUpdateHotel(
    Number(hotelId)
  );

  const { isSuperAdmin } = useContext(UserContext);
  const config = useContext(ConfigContext);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const { data: countries } = useCountries();
  const [originalBillingEmails, setOriginalBillingsEmails] = useState(
    hotel?.billingEmails
  );

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  useEffect(() => {
    if (document.readyState === 'complete') {
      const query = new URLSearchParams(location.search);
      const successfullyMerge = query.get('successfullyMerge');

      if (successfullyMerge) {
        message.success('Your hotels will be successfully merged very soon');
        query.delete('successfullyMerge');
        history.replace({ search: query.toString() });
      }
    }
  }, [history, location.search]);

  const isInstanceOfApiError = editHotelError instanceof ApiError;

  const hotelError =
    isInstanceOfApiError && editHotelError.jsonObject
      ? retrieveHotelErrorMessage(
          editHotelError.jsonObject.errorCode as HotelErrorCode
        )
      : { errorTitle: '', errorMessage: '' };

  const [form] = Form.useForm();
  const [menu, setMenu] = useState<MenuItem>(MENU_ITEMS[0]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');

    if (!tab) {
      return;
    }

    const target = MENU_ITEMS.find(
      (item) => item.name.toLowerCase() === tab.toLowerCase()
    );

    if (target) {
      setMenu(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to run only once on mount, not when location.search changes
  }, []);

  useEffect(() => {
    form.resetFields();
  }, [form, hotel]);

  useEffect(() => {
    if (!isFetchHotelLoading) {
      setOriginalBillingsEmails(hotel?.billingEmails);
    }
  }, [isFetchHotelLoading, hotel?.billingEmails]);

  const renderMenu = () => (
    <MenuCustom
      menuItems={MENU_ITEMS}
      selectedMenu={menu}
      setSelectedMenu={(menuItem: MenuItem) => {
        setMenu(menuItem);
        enterViewMode();
      }}
    />
  );

  const dispatchMenuSection = () => {
    switch (menu.name) {
      case MenuItemName.Finance:
        return (
          <Finance
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      case MenuItemName.Copywriting:
        return (
          <Copywriting
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      case MenuItemName.Services:
        return (
          <Services
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Spaces:
        return (
          <Spaces
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Brief:
        return (
          <MarketingBrief
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Cover:
        if (!hotel) {
          return null;
        }

        return (
          <CoverAndDiscover
            hotelId={hotel.id}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );

      case MenuItemName.PackagesRoomsAmenities:
        return (
          <PackagesRoomsAmenities
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = useCallback(
    async (values: Hotel) => {
      const emails = values.notifEmails.map(({ email }) => email);
      const hasDuplicateEmails = new Set(emails).size !== emails.length;

      if (hasDuplicateEmails) {
        message.error('Duplicate emails not allowed');

        return;
      }

      await updateHotel({
        ...hotel,
        ...values,
        location: {
          ...hotel?.location,
          ...values.location,
        },
        address: {
          ...hotel?.address,
          ...values.address,
          subCity:
            values.address?.subCity?.trim() === ''
              ? undefined
              : values.address?.subCity?.trim(),
        },
        notifEmails: values.notifEmails,
        channelManager:
          values.channelManager === ChannelManager.NONE
            ? undefined
            : values.channelManager,
        channelManagerCredentials:
          values.channelManager === ChannelManager.MEWS
            ? values.channelManagerCredentials
            : undefined,
        rateMode:
          values.channelManager === undefined ||
          values.channelManager === ChannelManager.NONE
            ? undefined
            : values.rateMode || RateMode.MULTI_RATE,
      });

      await reloadHotel();
      enterViewMode();
    },
    [updateHotel, hotel, reloadHotel, enterViewMode]
  );

  const submitForm = () => form.submit();

  if (!hotel || hotelId !== `${hotel.id}`) {
    return null;
  }

  const renderMergeButton = (hId: string) => (
    <div key={'merge-button'} className="merge-btn">
      <Button
        size="large"
        onClick={() => {
          window.open(`/hotel-migration/${hId}`, '_blank');
        }}
      >
        Merge
      </Button>
    </div>
  );

  const renderDuplicateButton = () => {
    if (hotel.isLegacy === false) {
      return null;
    }

    return (
      <div key={'duplicate-button'} className="duplicate-btn">
        <Button size="large" onClick={() => setDuplicateModalOpen(true)}>
          Duplicate
        </Button>
      </div>
    );
  };

  const renderPreviewButton = () => {
    if (!hotel || !hotel.slug) {
      return null;
    }

    const locale =
      countries?.find((country) => country.id === hotel.countryId)?.locale ??
      'fr';

    return (
      <div key={'preview-button'} className="preview-btn">
        <Button
          size="large"
          onClick={() =>
            window.open(
              `${config?.appUrl}/${locale}/hotels/${hotel.slug}`,
              '_blank'
            )
          }
        >
          Preview
        </Button>
      </div>
    );
  };

  const renderMigrateButton = () => {
    if (hotel.isLegacy === false || !hotel || !hotel.slug) {
      return null;
    }

    const isParentHotel = hotel.id === hotel.parentHotelId;
    const isCmHotel =
      hotel.stockScrapperEnabled === false &&
      hotel.channelManager !== undefined &&
      hotel.channelManager !== ChannelManager.NONE;
    const isSingleRate = hotel.rateMode === RateMode.SINGLE_RATE;

    const publishedPacks =
      packagesList?.packages?.filter((p) => p.published && !p.hidden) ?? [];
    const hasDefaultPricingRule =
      publishedPacks.length > 0 &&
      publishedPacks.every((p) => Boolean(p.autoDiscountRate));

    const disabledReason = () => {
      if (isSuperAdmin === false) {
        return 'Only super admins can migrate hotels.';
      }

      if (!isParentHotel) {
        return 'Only parent hotels can be migrated.';
      }

      if (!isCmHotel) {
        return 'Only channel-manager hotels can be migrated.';
      }

      if (!isSingleRate) {
        return 'Only single-rate hotels can be migrated.';
      }

      if (!hasDefaultPricingRule) {
        return 'Only hotels with published packages that all have a default pricing rule can be migrated.';
      }

      return undefined;
    };

    const canMigrate =
      isSuperAdmin &&
      isParentHotel &&
      isCmHotel &&
      isSingleRate &&
      hasDefaultPricingRule;

    return (
      <div key="migrate-button" className="preview-btn">
        <Tooltip title={disabledReason()}>
          <Button
            disabled={!canMigrate}
            size="large"
            onClick={() => setMigrateModalOpen(true)}
          >
            Migrate
          </Button>
        </Tooltip>
      </div>
    );
  };

  const shouldShowMergeButton =
    (hotel.id === hotel.parentHotelId ||
      hotel.stockScrapperEnabled ||
      (hotel.channelManager && hotel.channelManager !== ChannelManager.NONE)) &&
    packagesList?.packages.some((pkg) => !pkg.dayPackage) &&
    hotel.isLegacy;

  const headerExtra = [
    renderMigrateButton(),
    renderPreviewButton(),
    shouldShowMergeButton ? renderMergeButton(hotelId) : null,
    renderDuplicateButton(),
  ];

  return (
    <Layout className="hotel-detail">
      {STANDALONE_MENUS.has(menu.name) ? (
        dispatchMenuSection()
      ) : (
        <FormLegacyRenderProp
          form={form}
          onReset={() => form.resetFields()}
          onFinish={handleSubmit}
          initialValues={hotel}
        >
          {(
            values,
            { resetFields, getFieldsError, setFieldsValue, validateFields }
          ) =>
            menu.name === MenuItemName.General ? (
              <SDetailLayout
                isDirty
                title={
                  <span className="hotel-title">
                    {hotel.name}
                    {hotel.isLegacy ? (
                      <Tag key="old-data-model" color="#f50">
                        Old data model
                      </Tag>
                    ) : (
                      <Tag key="new-data-model" color="#87d068">
                        New data model
                      </Tag>
                    )}
                  </span>
                }
                mode={mode}
                onEdit={enterEditMode}
                onSave={submitForm}
                onCancel={() => {
                  resetFields();
                  validateFields();
                  enterViewMode();
                }}
                reset={() => {
                  resetFields();
                  validateFields();
                }}
                headerMenu={renderMenu()}
                saveDisabled={
                  !getFieldsError().every(
                    ({ errors }) => errors.length === 0
                  ) ||
                  values.notifEmails.every(
                    ({ bookingConfirmation }) => !bookingConfirmation
                  )
                }
                headerExtra={[...headerExtra]}
              >
                <Content className="hotel-detail__content">
                  <BasicInfo
                    values={values}
                    setFieldsValue={setFieldsValue}
                    originalBillingEmails={originalBillingEmails}
                  />
                </Content>
              </SDetailLayout>
            ) : (
              dispatchMenuSection()
            )
          }
        </FormLegacyRenderProp>
      )}

      <Notification
        title={hotelError.errorTitle}
        content={hotelError.errorMessage}
        visible={isInstanceOfApiError}
        type="error"
        hasCancelButton={false}
        centered
      />

      <DuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        hotelId={hotelId}
      />

      <MigrateModal
        open={migrateModalOpen}
        onClose={() => setMigrateModalOpen(false)}
        hotel={hotel}
      />
    </Layout>
  );
};

export const HotelEdit = () => {
  return (
    <ViewEditModeContextProvider>
      <HotelEditContent />
    </ViewEditModeContextProvider>
  );
};
